import { defineQuery } from '@rocicorp/zero';
import * as z from 'zod';
import { CatalogType, ConsumableCategory } from '../database/schema.js';
import { zql } from '../schema.js';
import { applyCursor } from '../util/apply-cursor.js';
import { Status } from '../util/schemas.js';
import { applyPopulate } from './populates.js';
import { Cursor, Sort } from './util.js';
// #region CONSUMABLES
export const ListConsumablesFiltersInput = z.object({
    search: z.string().optional(),
    status: z.array(Status).optional(),
    categories: z.array(ConsumableCategory).optional(),
});
export const ListConsumablesInput = z.object({
    filters: ListConsumablesFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
        name: z.string(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
});
// #endregion
// #region TRADE NAMES
export const ListTradeNamesFiltersInput = z.object({
    search: z.string().optional(),
    status: z.array(Status).optional(),
    types: z.array(CatalogType).optional(),
    forms: z.array(z.uuidv7()).optional(),
    substances: z.array(z.uuidv7()).optional(),
});
export const ListTradeNamesInput = z.object({
    filters: ListTradeNamesFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
        name: z.string(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
    populate: z.enum(['minimal', 'list', 'full']).default('list'),
});
// #endregion
// #region PRESCRIBED NAMES
export const ListPrescribedNamesFiltersInput = z.object({
    drugCatalogItemId: z.string().optional(),
    search: z.string().optional(),
    /** only products that can still be dispensed: with at least one active trade name (CY-259) */
    withActiveTradeName: z.boolean().optional(),
});
export const ListPrescribedNamesInput = z.object({
    filters: ListPrescribedNamesFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
        name: z.string(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
});
// #endregion
// #region UNITS
export const ListUnitsFiltersInput = z.object({
    type: z.enum([
        'substance.dose',
        'tradeName.strength',
        'tradeName.volume',
        'tradeName.concentration',
        'mass',
        'volume',
        'concentration',
    ]),
});
export const ListUnitsInput = z.object({
    filters: ListUnitsFiltersInput.optional(),
    limit: z.number().optional(),
});
const ResourcesToUcumCodesMap = {
    'substance.dose': ['mg/m2', 'mg/kg', 'mg', 'g', 'ug', '[IU]', 'mL', 'mg/mL/min'],
    'tradeName.strength': ['mg', 'g', 'ug', 'mg/mL', '%', 'mmol/L', '[IU]', '10*6.[IU]'],
    'tradeName.volume': ['mL'],
    'tradeName.concentration': ['mg/mL', 'mg/dL', 'g/dL', 'mmol/L', 'U/L', '%'],
    mass: ['mg', 'g', 'ug', '[IU]'],
    volume: ['mL'],
    concentration: ['mg/mL', '[IU]/mL', 'g/mL', '%'],
};
// #endregion
// #region POPULATE PROFILES
/**
 * Populate profiles for trade name queries.
 *
 * - `minimal`: No relations. Use for dropdowns that only need `id` + `name`.
 * - `list`: medicationProfile (with drugCatalogItem, medicineForm), strengthUnit.
 * - `full`: Everything including reconstitutionSolvents, consumables, stabilityRules, batches.
 */
export const tradeNamePopulates = {
    minimal: null,
    list: q => q
        .related('medicationProfile', profile => profile.related('drugCatalogItem').related('medicineForm'))
        .related('strengthUnit'),
    full: q => q
        .related('created')
        .related('prescribedName')
        .related('reconstitutionSolvents', reconSolvent => reconSolvent
        .related('solutionDrug', solutionDrug => solutionDrug.where('deletedAt', 'IS', null))
        .where('deletedAt', 'IS', null))
        .related('consumables', consumable => consumable.related('consumable', c => c.where('deletedAt', 'IS', null)).where('deletedAt', 'IS', null))
        .related('medicationProfile', profile => profile
        .related('drugCatalogItem')
        .related('medicineForm')
        .related('stabilityRules', rules => rules.where('deletedAt', 'IS', null)))
        .related('stabilityRules', rule => rule.related('stabilityRule', r => r.related('storageLabel')).where('deletedAt', 'IS', null))
        .related('concentrationUnit')
        .related('reconstitutedConcentrationUnit')
        .related('containerMaterial')
        .related('containerType')
        .related('strengthUnit')
        .related('manufacturer')
        .related('batches', batch => batch.where('deletedAt', 'IS', null).related('stockLocation').related('supplierCompany')),
};
// #endregion
export const productsQueries = {
    listConsumables: defineQuery(ListConsumablesInput, ({ args }) => {
        let q = zql.consumables.where('deletedAt', 'IS', null);
        if (args.filters) {
            const { status, categories } = args.filters;
            const search = args.filters.search?.trim();
            if (search) {
                q = q.where(({ cmp, or }) => or(cmp('name', 'ILIKE', `%${search}%`), cmp('description', 'ILIKE', `%${search}%`)));
            }
            if (status?.length) {
                q = q.where('status', 'IN', status);
            }
            if (categories?.length) {
                q = q.where('category', 'IN', categories);
            }
        }
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        return q;
    }),
    getConsumableById: defineQuery(z.string(), ({ args }) => {
        return zql.consumables
            .related('created')
            .related('batches', batch => batch.where('deletedAt', 'IS', null).related('stockLocation').related('supplierCompany'))
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getAllConsumables: defineQuery(() => {
        return zql.consumables.related('created').where('deletedAt', 'IS', null);
    }),
    getConsumableActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', 'consumable')
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
    listTradeNamesDrugs: defineQuery(() => {
        return zql.drugCatalogItems
            .whereExists('medicationProfiles', profiles => profiles.whereExists('tradeNames'))
            .where('deletedAt', 'IS', null)
            .orderBy('name', 'asc');
    }),
    listTradeNamesMedicationProfiles: defineQuery(() => {
        return zql.medicationProfiles
            .whereExists('tradeNames', t => t.where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null)
            .orderBy('name', 'asc');
    }),
    listTradeNamesMedicineForms: defineQuery(() => {
        return zql.medicineForms
            .where('deletedAt', 'IS', null)
            .whereExists('medicationProfiles', m => m.where('deletedAt', 'IS', null).whereExists('tradeNames', t => t.where('deletedAt', 'IS', null)))
            .orderBy('name', 'asc');
    }),
    listTradeNames: defineQuery(ListTradeNamesInput, ({ args }) => {
        let q = zql.tradeNames.where('deletedAt', 'IS', null);
        if (args.filters) {
            const { status, substances, types, forms } = args.filters;
            const search = args.filters.search?.trim();
            if (search) {
                q = q.where(({ cmp, or }) => or(cmp('name', 'ILIKE', `%${search}%`), cmp('nationalId', 'ILIKE', `%${search}%`), cmp('barcodeGtin', 'ILIKE', `%${search}%`)));
            }
            if (status?.length) {
                q = q.where('status', 'IN', status);
            }
            if (forms?.length || substances?.length || types?.length) {
                q = q.whereExists('medicationProfile', profile => {
                    if (forms?.length)
                        profile = profile.where('medicineFormId', 'IN', forms);
                    if (substances?.length)
                        profile = profile.where('drugCatalogItemId', 'IN', substances);
                    if (types?.length)
                        profile = profile.whereExists('drugCatalogItem', drug => drug.where('catalogType', 'IN', types));
                    return profile;
                });
            }
        }
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        q = applyPopulate(q, tradeNamePopulates, args.populate);
        return q;
    }),
    getAllTradeNames: defineQuery(_params => {
        let q = zql.tradeNames.where('deletedAt', 'IS', null);
        q = applyPopulate(q, tradeNamePopulates, 'minimal');
        return q;
    }),
    getTradeNameById: defineQuery(z.string(), ({ args }) => {
        let q = zql.tradeNames.where('id', '=', args).where('deletedAt', 'IS', null);
        q = applyPopulate(q, tradeNamePopulates, 'full');
        return q.one();
    }),
    getTradeNameActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', 'trade-name')
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
    getPrescribedNameActivitiesById: defineQuery(z.string(), ({ args }) => {
        return zql.eventLogs
            .related('actor')
            .where('resource', '=', 'prescribed-name')
            .where('resourceId', '=', args)
            .orderBy('performedAt', 'desc');
    }),
    listUnits: defineQuery(ListUnitsInput, ({ args }) => {
        let q = zql.units.orderBy('ucumCode', 'asc');
        if (args.filters) {
            const { type } = args.filters;
            const availableValues = ResourcesToUcumCodesMap[type] || [];
            q = q.where('ucumCode', 'IN', availableValues);
        }
        return q;
    }),
    listPrescribedNames: defineQuery(ListPrescribedNamesInput, ({ args }) => {
        let q = zql.prescribedNames.where('deletedAt', 'IS', null);
        if (args.filters) {
            const { drugCatalogItemId } = args.filters;
            const search = args.filters.search?.trim();
            if (search) {
                q = q.where('name', 'ILIKE', `%${search}%`);
            }
            if (drugCatalogItemId) {
                q = q.where('drugCatalogItemId', '=', drugCatalogItemId);
            }
            if (args.filters.withActiveTradeName) {
                q = q.whereExists('tradeNames', tn => tn.where('status', '=', 'active').where('deletedAt', 'IS', null));
            }
        }
        q = q.related('drugCatalogItem', drug => drug
            .related('activeSubstanceDetails', details => details.related('doseUnit'))
            .related('supportiveMedicationDetails', details => details.related('doseUnit')));
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        return q;
    }),
    getPrescribeNameById: defineQuery(z.string(), ({ args }) => {
        let q = zql.prescribedNames.where('id', '=', args).where('deletedAt', 'IS', null);
        q = q.related('drugCatalogItem', drug => drug
            .related('activeSubstanceDetails', details => details.related('doseUnit'))
            .related('supportiveMedicationDetails', details => details.related('doseUnit')));
        // strength/reconstitution data on the trade names drives the concentration preview
        q = q.related('tradeNames', tn => tn
            .where('deletedAt', 'IS', null)
            .related('reconstitutedConcentrationUnit')
            .related('reconstitutionSolvents', rs => rs.where('deletedAt', 'IS', null).related('solutionDrug').where('deletedAt', 'IS', null))
            .related('medicationProfile', mp => mp.where('deletedAt', 'IS', null)));
        return q.one();
    }),
    // a product without an active trade name can never be stocked; the picking list says so (CY-259)
    getPrescribedNamesActiveTradeNames: defineQuery(z.array(z.string()), ({ args }) => {
        return zql.prescribedNames
            .where('id', 'IN', args)
            .related('tradeNames', tn => tn.where('status', '=', 'active').where('deletedAt', 'IS', null));
    }),
    getPrescribedNamesWithBatches: defineQuery(z.string(), ({ args }) => {
        // only trade names of this substance count, so another drug's vials never show as available (CY-368);
        // inactive trade names are never offered, and a product without an active one is left out (CY-259)
        let q2 = zql.prescribedNames
            .related('tradeNames', tn => tn
            .where('status', '=', 'active')
            .where('deletedAt', 'IS', null)
            .whereExists('medicationProfile', profile => profile.where('drugCatalogItemId', '=', args))
            .related('batches', b => b.where('deletedAt', 'IS', null)))
            .whereExists('tradeNames', tn => tn.where('status', '=', 'active').where('deletedAt', 'IS', null))
            .where('deletedAt', 'IS', null)
            .where('drugCatalogItemId', '=', args);
        return q2;
    }),
};
//# sourceMappingURL=products.queries.js.map